/**
 * Ranking, tiebreaks and awards — shared by every format.
 *
 * Formats differ in how they produce a number. They do not differ in how that
 * number becomes a finishing place and a payout, so that lives here once.
 */

import type {
  Award,
  MatchId,
  PlacePayout,
  PlayerId,
  TiebreakChain,
  TiebreakCounters,
  TiebreakResolution,
  UnitTab,
} from './types'

// --- deterministic RNG ------------------------------------------------------

export function hash32(str: string): number {
  let h = 2166136261 >>> 0
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i)
    h = Math.imul(h, 16777619) >>> 0
  }
  return h >>> 0
}

export function mulberry32(seed: number): () => number {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

// --- tiebreaks --------------------------------------------------------------

export interface TiebreakContext {
  /** Already-decided ties, so a coin flip never changes on recompute. */
  existing: TiebreakResolution[]
  /** Finishing place per player in a given round seq, for `placeInRound`. */
  placeInRound?: (roundSeq: number, playerId: PlayerId) => number | null
  seed: string
}

const KEY = (scope: string, ids: PlayerId[]) => `${scope}|${[...ids].sort().join(',')}`

/**
 * Sort ids into best-first buckets by a numeric value, grouping equal values
 * together. A step rarely separates everyone at once — the buckets are what
 * let the caller recurse the rest of the chain into whoever is still tied
 * instead of freezing them in arrival order.
 */
const bucketBy = (
  ids: PlayerId[],
  vals: Map<PlayerId, number>,
  cmp: (a: number, b: number) => number,
): PlayerId[][] => {
  const sorted = [...ids].sort((a, b) => cmp(vals.get(a)!, vals.get(b)!))
  const buckets: PlayerId[][] = []
  for (const id of sorted) {
    const last = buckets[buckets.length - 1]
    if (last && vals.get(last[0]!) === vals.get(id)) last.push(id)
    else buckets.push([id])
  }
  return buckets
}

/**
 * A step only ever tells you about the buckets it produced, not about the
 * players still tied inside one — those get handed the rest of the chain via
 * `breakTie` itself, so a later step (or the coin flip) still gets consulted
 * instead of the group falling back to whatever order it arrived in.
 *
 * The nested resolutions are COLLECTED, not discarded. This used to keep only
 * the sub-tie's order, which made a coin flip hidden inside a bigger tie
 * invisible: three men tied on points, birdies splitting one off, and the
 * remaining pair "resolved" by a flip nobody could see, alert on, or record.
 * The pending-flip banner and the recorded-ceremony lookup both key on the
 * resolution existing, so it has to surface.
 */
const resolveBuckets = (
  buckets: PlayerId[][],
  tied: { playerId: PlayerId; counters: TiebreakCounters; netTotal: number }[],
  rest: TiebreakChain,
  scope: string,
  ctx: TiebreakContext,
  nested: TiebreakResolution[],
): PlayerId[] => {
  const order: PlayerId[] = []
  for (const bucket of buckets) {
    if (bucket.length === 1) {
      order.push(bucket[0]!)
      continue
    }
    const subTied = tied.filter((t) => bucket.includes(t.playerId))
    const sub = breakTie(scope, subTied, rest, ctx)
    nested.push(...sub.resolutions)
    order.push(...sub.order)
  }
  return order
}

/**
 * Order a set of tied players.
 *
 * Every step is applied in sequence. A step that separates only part of the
 * group does not end the chain for the rest — whoever it left tied is
 * recursed through the remaining steps, otherwise they'd be ordered by
 * whatever position they happened to arrive in (which traces back to roster
 * order, not merit). A coin flip is the last resort and is RECORDED — if it
 * were re-rolled on each recompute the leaderboard would quietly reshuffle
 * itself between refreshes.
 */
export function breakTie(
  scope: string,
  tied: { playerId: PlayerId; counters: TiebreakCounters; netTotal: number }[],
  chain: TiebreakChain,
  ctx: TiebreakContext,
): { order: PlayerId[]; resolutions: TiebreakResolution[] } {
  const ids = tied.map((t) => t.playerId)
  const prior = ctx.existing.find((r) => KEY(r.scope, r.contenders) === KEY(scope, ids))
  if (prior) return { order: prior.order, resolutions: [prior] }

  for (let i = 0; i < chain.steps.length; i++) {
    const step = chain.steps[i]!
    const rest: TiebreakChain = { steps: chain.steps.slice(i + 1) }

    if (step.kind === 'countOf') {
      /*
       * "Fewest bogeys" has to mean the better card, not literally the count of
       * holes exactly one over.
       *
       * Read literally the step was not monotone and the exploit was free:
       * eight triple bogeys beat eight bogeys outright, because a triple lands
       * in `worse` and the step only looked at `bogeys`. Playing worse golf won.
       *
       * The counters only bucket by category, so the finest comparison
       * available is: fewest holes worse than a bogey first, then fewest
       * bogeys. That makes eight bogeys beat eight triples, keeps a clean card
       * ahead of a ragged one, and — the point — means turning a triple into a
       * bogey can never cost you the tiebreak. Encoded as one sortable number
       * because `worse` is bounded by the eighteen holes of a round.
       */
      const countFor = (c: TiebreakCounters): number =>
        step.what === 'bogeys' ? c.worse * 100 + c.bogeys : c[step.what]
      const vals = new Map(tied.map((t) => [t.playerId, countFor(t.counters)]))
      /*
       * The note names the count that actually decided it.
       *
       * Printing both numbers for every tie was noise, and printing only the
       * bogeys would be a lie in the case where holes worse than a bogey were
       * what separated them. So it says whichever one broke the tie.
       */
      const byWorse =
        step.what === 'bogeys' && new Set(tied.map((t) => t.counters.worse)).size > 1
      const stepLabel = byWorse ? 'holes worse than a bogey' : step.what
      const shown = new Map(
        tied.map((t) => [
          t.playerId,
          String(byWorse ? t.counters.worse : t.counters[step.what]),
        ]),
      )
      const distinct = new Set(vals.values())
      if (distinct.size > 1) {
        // Bogeys is the one counted category where fewer wins; the chain is
        // conventionally "most eagles/birdies/pars, FEWEST bogeys". The
        // direction lives here, keyed off `what`, rather than as a flag on
        // the step, since it's a property of the category, not a per-chain
        // choice — a chain built with 'bogeys' should never need to remember
        // to also say "ascending".
        const cmp: (a: number, b: number) => number =
          step.what === 'bogeys' ? (a, b) => a - b : (a, b) => b - a
        const nested: TiebreakResolution[] = []
        const order = resolveBuckets(bucketBy(ids, vals, cmp), tied, rest, scope, ctx, nested)
        return {
          order,
          resolutions: [
            {
              scope,
              contenders: [...ids].sort(),
              resolvedBy: 'countOf',
              detail: `${stepLabel}: ${order.map((id) => `${id} ${shown.get(id)}`).join(', ')}`,
              order,
            },
            ...nested,
          ],
        }
      }
    }

    if (step.kind === 'placeInRound' && ctx.placeInRound) {
      const raw = new Map(ids.map((id) => [id, ctx.placeInRound!(step.roundSeq, id)]))
      if ([...raw.values()].every((v) => v != null) && new Set(raw.values()).size > 1) {
        const vals = raw as Map<PlayerId, number>
        const nested: TiebreakResolution[] = []
        const order = resolveBuckets(bucketBy(ids, vals, (a, b) => a - b), tied, rest, scope, ctx, nested)
        return {
          order,
          resolutions: [
            {
              scope,
              contenders: [...ids].sort(),
              resolvedBy: 'placeInRound',
              detail: `place in round ${step.roundSeq}`,
              order,
            },
            ...nested,
          ],
        }
      }
    }

    if (step.kind === 'lowestNetTotal') {
      const vals = new Map(tied.map((t) => [t.playerId, t.netTotal]))
      if (new Set(vals.values()).size > 1) {
        const nested: TiebreakResolution[] = []
        const order = resolveBuckets(bucketBy(ids, vals, (a, b) => a - b), tied, rest, scope, ctx, nested)
        return {
          order,
          resolutions: [
            {
              scope,
              contenders: [...ids].sort(),
              resolvedBy: 'lowestNetTotal',
              detail: 'lowest net total',
              order,
            },
            ...nested,
          ],
        }
      }
    }

    if (step.kind === 'coinFlip') break
  }

  /*
   * Seeded so it is reproducible, then persisted so it is permanent.
   *
   * Sorted before the draws are handed out, and that is the whole point. The
   * KEY above already sorts, so the same tie always seeded the same RNG — but
   * the draws were then assigned by walking the ids in ARRIVAL order, which for
   * the championship is the order men sit in their foursomes. Tapping "Fill
   * flights" rewrites those arrays into seeding order, so the same tie under
   * the same seed resolved differently before and after the tap: $1,250 moved
   * between two men without a single stroke changing.
   *
   * A flip may depend on who is tied and on the seed. It may not depend on
   * where they happened to be sitting.
   */
  const rng = mulberry32(hash32(`${ctx.seed}|${KEY(scope, ids)}`))
  const order = [...ids]
    .sort()
    .map((id) => ({ id, r: rng() }))
    .sort((a, b) => a.r - b.r)
    .map((x) => x.id)
  return {
    order,
    resolutions: [
      {
        scope,
        contenders: [...ids].sort(),
        resolvedBy: 'coinFlip',
        detail: 'coin flip',
        order,
      },
    ],
  }
}

// --- ranking and awards -----------------------------------------------------

export function payoutForPlace(payouts: PlacePayout[], place: number): PlacePayout | null {
  return payouts.find((p) => place >= p.from && place <= p.to) ?? null
}

export function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0]!)
}

/**
 * Rank tabs (higher `rankValue` is better) and turn placements into awards.
 * Ties are separated by the chain, so two players never share a place — the
 * payouts are stepped and sharing one would silently mis-pay the match.
 */
export function rankAndAward(
  matchId: MatchId,
  tabs: UnitTab[],
  payouts: PlacePayout[],
  chain: TiebreakChain,
  ctx: TiebreakContext,
  scope: string,
): { awards: Award[]; tiebreaks: TiebreakResolution[]; order: UnitTab[] } {
  const sorted = [...tabs].sort((a, b) => b.rankValue - a.rankValue)
  const groups: UnitTab[][] = []
  for (const t of sorted) {
    const last = groups[groups.length - 1]
    if (last && last[0]!.rankValue === t.rankValue) last.push(t)
    else groups.push([t])
  }

  const order: UnitTab[] = []
  const tiebreaks: TiebreakResolution[] = []
  for (const tied of groups) {
    if (tied.length === 1) {
      order.push(tied[0]!)
      continue
    }
    const entries = tied.map((t) => ({
      playerId: t.playerIds[0]!,
      counters: t.counters[t.playerIds[0]!] ?? {
        eagles: 0,
        birdies: 0,
        pars: 0,
        bogeys: 0,
        worse: 0,
      },
      netTotal: t.detail.kind === 'strokes' ? t.detail.netTotal : 0,
    }))
    const { order: ids, resolutions } = breakTie(scope, entries, chain, ctx)
    tiebreaks.push(...resolutions)
    for (const id of ids) {
      const found = tied.find((t) => t.playerIds[0] === id)
      if (found) order.push(found)
    }
  }

  const awards: Award[] = []
  order.forEach((tab, i) => {
    const place = i + 1
    const p = payoutForPlace(payouts, place)
    for (const pid of tab.playerIds) {
      awards.push({
        playerId: pid,
        matchId,
        place,
        points: p?.points ?? 0,
        cash: p?.cash ?? 0,
        label: ordinal(place),
      })
    }
  })

  return { awards, tiebreaks, order }
}
